/**
 * Subscription Protection
 *
 * Ensures devices always have a subscription by intercepting subscription
 * removal at the fetch level and atomically replacing with Flexi plan.
 *
 * Uses fetch interception to avoid timing issues with event listeners.
 */

import { morphSection } from './section-renderer.js';

class SubscriptionProtection {
  constructor() {
    this.flexiPlanId = null;
    this.flexiSellingPlan = null;
    this.processing = false;
    this.originalFetch = window.fetch.bind(window);
  }

  init(flexiPlanId, flexiSellingPlan) {
    this.flexiPlanId = flexiPlanId;
    this.flexiSellingPlan = flexiSellingPlan;

    // Intercept fetch requests to cart change endpoints
    this.interceptFetch();
  }

  interceptFetch() {
    const self = this;

    window.fetch = function(...args) {
      const [url, options] = args;

      // Check if this is a cart change request
      if ((url.includes('/cart/change') || url === '/cart/change.js') && options?.method === 'POST') {
        return self.handleCartChange(url, options);
      }

      // Pass through all other requests
      return self.originalFetch(...args);
    };
  }

  async handleCartChange(url, options) {
    // Parse the request body to get line number and quantity
    let body;
    try {
      body = JSON.parse(options.body);
    } catch (e) {
      console.error('[Subscription Protection] Failed to parse request body:', e);
      return this.originalFetch(url, options);
    }

    // Only intercept quantity=0 (removals)
    if (body.quantity !== 0) {
      return this.originalFetch(url, options);
    }

    const lineNumber = body.line;

    // Get current cart to check what's being removed
    const currentCartResponse = await this.originalFetch('/cart.js');
    const currentCart = await currentCartResponse.json();

    const itemBeingRemoved = currentCart.items[lineNumber - 1];

    if (!itemBeingRemoved) {
      console.error('[Subscription Protection] Item not found at line', lineNumber);
      return this.originalFetch(url, options);
    }

    // Check if this is a subscription
    const isSubscription = itemBeingRemoved.properties?._subscription === 'true';
    const isFlexi = itemBeingRemoved.product_title?.includes('Flexi');

    // If removing main product (not subscription), also remove related subscription items
    if (!isSubscription) {
      const mainProductVariantId = itemBeingRemoved.variant_id;

      // Find related subscription items with matching device_variant
      const relatedItems = currentCart.items.filter((item, index) => {
        if (index === lineNumber - 1) return false; // Skip the main product itself
        return item.properties?._device_variant === String(mainProductVariantId);
      });

      if (relatedItems.length > 0) {
        // Add removing animation to ALL cart items
        const cartItemRows = document.querySelectorAll('.cart-items__table-row[data-key]');
        cartItemRows.forEach(row => {
          row.style.setProperty('--row-height', `${row.clientHeight}px`);
          row.classList.add('removing');
        });

        // Wait for animation to complete (CSS animation duration is 300ms)
        await new Promise(resolve => setTimeout(resolve, 300));

        // Clear cart
        await this.removeMainProductWithRelated(currentCart, lineNumber, relatedItems);

        // Use the SAME approach as theme for last item removal
        const cartItemsComponent = document.querySelector('cart-items-component');
        const template = document.getElementById('empty-cart-template');

        if (cartItemsComponent && template instanceof HTMLTemplateElement) {
          const clone = document.importNode(template.content, true);

          // Use startViewTransition if available (same as theme)
          const startViewTransition = window.startViewTransition || function(callback) { callback(); };
          const isDrawer = cartItemsComponent.classList.contains('is-drawer') ||
                           cartItemsComponent.closest('.cart-drawer');

          startViewTransition(() => {
            cartItemsComponent.replaceChildren(clone);
          }, [isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

          // Watch for checkout button to reappear and ensure it's initialized
          const observer = new MutationObserver(() => {
            const uninitializedButtons = document.querySelectorAll('[data-validate-checkout]:not([data-validation-initialized])');

            if (uninitializedButtons.length > 0) {
              document.dispatchEvent(new CustomEvent('shopify:section:load'));

              setTimeout(() => {
                const stillUninitialized = document.querySelectorAll('[data-validate-checkout]:not([data-validation-initialized])');
                if (stillUninitialized.length === 0) {
                  observer.disconnect();
                }
              }, 100);
            }
          });

          observer.observe(document.body, {
            childList: true,
            subtree: true
          });

          setTimeout(() => observer.disconnect(), 10000);
        }

        // Fetch updated sections for the response
        const sectionId = body.sections?.split(',')[0];
        const sectionsResponse = await this.originalFetch(
          `/cart?sections=${body.sections}&sections_url=${body.sections_url || window.location.pathname}`
        );
        const sectionsData = await sectionsResponse.text();
        const updatedSections = JSON.parse(sectionsData);

        // Update cart counter to 0
        this.updateCartCounter(0);

        // Return proper response
        return new Response(JSON.stringify({
          token: '',
          item_count: 0,
          items: [],
          sections: updatedSections
        }), {
          status: 200,
          headers: { 'Content-Type': 'application/json' }
        });
      }

      // No related items, allow normal removal
      return this.originalFetch(url, options);
    }

    // Check if this is a non-Flexi subscription
    if (!isSubscription || isFlexi) {
      // Not a subscription or it's Flexi - allow normal removal
      return this.originalFetch(url, options);
    }

    // This is a non-Flexi subscription removal - intercept and replace
    if (this.processing) {
      return this.originalFetch(url, options);
    }

    this.processing = true;

    // Add loading state to the item being replaced
    this.addLoadingState(lineNumber);

    try {
      // Get sections from original request to include in mock response
      const requestedSections = body.sections;

      // Fetch current sections to return unchanged
      let currentSections = {};
      if (requestedSections) {
        const sectionsResponse = await this.originalFetch(
          `/cart?sections=${requestedSections}&sections_url=${body.sections_url || window.location.pathname}`
        );
        const sectionsData = await sectionsResponse.text();
        const parsed = JSON.parse(sectionsData);
        currentSections = parsed;
      }

      // Instead of calling the original removal, we'll do the replacement
      await this.replaceSubscription(currentCart, itemBeingRemoved, lineNumber);

      // Return a mock response WITH current sections
      // This prevents visual flicker by keeping the UI unchanged during replacement
      return new Response(JSON.stringify({
        ...currentCart,
        item_count: currentCart.item_count,
        sections: currentSections,
        note: 'Replaced by subscription protection'
      }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      console.error('[Subscription Protection] Replacement failed:', error);
      this.removeLoadingState();
      // Fall back to normal removal
      return this.originalFetch(url, options);
    } finally {
      this.processing = false;
    }
  }

  addLoadingState(lineNumber) {
    // Find actual cart item rows (not header row)
    const cartItems = document.querySelectorAll('.cart-items__table-row[data-key]');
    const itemRow = cartItems[lineNumber - 1];

    if (!itemRow) {
      console.error('[Subscription Protection] Item row not found for line:', lineNumber);
      return;
    }

    itemRow.dataset.replacing = 'true';

    // Apply shimmer to text-components (price, etc.)
    const textComponents = itemRow.querySelectorAll('text-component');
    textComponents.forEach(tc => tc.setAttribute('shimmer', ''));

    // Apply shimmer to remove button
    const removeButton = itemRow.querySelector('button[aria-label*="Remove"]');

    if (removeButton) {
      removeButton.disabled = true;
      removeButton.classList.add('subscription-shimmer');

      // Add shimmer CSS if not already present
      if (!document.getElementById('subscription-shimmer-styles')) {
        const style = document.createElement('style');
        style.id = 'subscription-shimmer-styles';
        style.textContent = `
          .subscription-shimmer {
            color: transparent !important;
            background-image: linear-gradient(
              -85deg,
              rgb(var(--color-foreground-rgb) / 0.5) 10%,
              rgb(var(--color-foreground-rgb) / 0.1) 50%,
              rgb(var(--color-foreground-rgb) / 0.5) 90%
            );
            background-clip: text;
            -webkit-background-clip: text;
            background-size: 200% 100%;
            background-position: 100% 0;
            animation: text-shimmer 1.25s infinite linear;
          }
        `;
        document.head.appendChild(style);
      }
    } else {
      console.error('[Subscription Protection] Remove button not found!');
    }
  }

  removeLoadingState() {
    // Remove loading state from all items (cleanup happens via morph, but this is for safety)
    const replacingItems = document.querySelectorAll('[data-replacing="true"]');
    replacingItems.forEach(item => {
      // Remove shimmer from text components
      const textComponents = item.querySelectorAll('text-component[shimmer]');
      textComponents.forEach(tc => tc.removeAttribute('shimmer'));

      // Remove shimmer from button
      const removeButton = item.querySelector('button.subscription-shimmer');
      if (removeButton) {
        removeButton.classList.remove('subscription-shimmer');
        removeButton.disabled = false;
      }

      delete item.dataset.replacing;
    });
  }

  getSectionIds() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];

    cartItemsComponents.forEach(component => {
      if (component.dataset.sectionId) {
        sectionIds.push(component.dataset.sectionId);
      }
    });

    return sectionIds;
  }

  async replaceSubscription(currentCart, subscriptionItem, lineNumber) {
    const deviceVariant = subscriptionItem.properties?._device_variant;

    if (!deviceVariant) {
      throw new Error('Could not find device variant in subscription properties');
    }

    // Get section IDs for cart update
    const sectionIds = this.getSectionIds();
    const sectionId = sectionIds[0];

    // Step 1: Remove old subscription
    await this.originalFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        line: lineNumber,
        quantity: 0
      })
    });

    // Step 2: Add Flexi subscription WITH sections
    const flexiPayload = {
      id: this.flexiPlanId,
      quantity: 1,
      selling_plan: this.flexiSellingPlan,
      properties: {
        _subscription: 'true',
        _device_variant: String(deviceVariant)
      }
    };

    const addResponse = await this.originalFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [flexiPayload],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname
      })
    });

    if (!addResponse.ok) {
      throw new Error('Failed to add Flexi subscription');
    }

    const cartWithSections = await addResponse.json();

    // Step 3: Morph section to update UI
    const sectionHtml = cartWithSections.sections?.[sectionId];

    if (sectionHtml) {
      const cartItemsComponent = document.querySelector('cart-items-component');
      const isDrawer = cartItemsComponent?.classList.contains('is-drawer') ||
                       cartItemsComponent?.closest('.cart-drawer');

      morphSection(sectionId, sectionHtml, isDrawer ? 'hydration' : 'full');

      // Remove loading state after morph completes
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        this.removeLoadingState();
      });
    } else {
      console.warn('[Subscription Protection] No section HTML in response');
      this.removeLoadingState();
    }

    // Step 4: Update cart counter with correct item count
    // Use double RAF to ensure update happens after all theme updates complete
    requestAnimationFrame(() => {
      requestAnimationFrame(async () => {
        const updatedCartResponse = await this.originalFetch('/cart.js');
        const updatedCart = await updatedCartResponse.json();
        this.updateCartCounter(updatedCart.item_count);
      });
    });
  }

  async removeMainProductWithRelated(currentCart, mainProductLine, relatedItems) {
    // Clear the entire cart
    await this.originalFetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
  }

  updateCartCounter(itemCount) {
    // Update the visually-hidden text for screen readers
    const hiddenText = document.querySelector('#cart-bubble-text .visually-hidden');
    if (hiddenText) {
      hiddenText.textContent = `Total items in cart: ${itemCount}`;
    }

    // Update the visible bubble count
    const bubbleCount = document.querySelector('[data-testid="cart-bubble"]');
    if (bubbleCount) {
      bubbleCount.textContent = itemCount;

      // Show/hide bubble based on count
      const cartBubble = bubbleCount.closest('.cart-bubble');
      if (cartBubble) {
        if (itemCount > 0) {
          cartBubble.classList.remove('visually-hidden');
          bubbleCount.classList.remove('hidden');
        } else {
          cartBubble.classList.add('visually-hidden');
          bubbleCount.classList.add('hidden');
        }
      }
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProtection);
} else {
  initProtection();
}

function initProtection() {
  const protectionEl = document.querySelector('subscription-protection');

  if (protectionEl) {
    const protection = new SubscriptionProtection();
    protection.init(
      protectionEl.dataset.flexiPlanId,
      protectionEl.dataset.flexiSellingPlan
    );
  }
}
